import React, { useState, useEffect, useRef } from "react";
import { Formik } from "formik";
import { useLocation, useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import Breadcrums from "../../../common/breadcrumbs";
import * as players from "../../../services/players.service";
import * as clubAdmin from "../../../services/clubAdmin.service";
import { SWAL_SETTINGS, INPUT_LENGTH_50 } from "../../../../../utils/Constants";
import {
  globalLoader,
  handleServerValidations,
} from "../../../../../utils/commonfunction";
import { useTranslation } from "react-i18next";
import CustomError from "../../../../../utils/customError";

const POSITIONS = [
  { key: "GS", label: "Goal Shooter" },
  { key: "GA", label: "Goal Attack" },
  { key: "WA", label: "Wing Attack" },
  { key: "C", label: "Center" },
  { key: "WD", label: "Wing Defense" },
  { key: "GD", label: "Goal Defense" },
  { key: "GK", label: "Goal Keeper" },
];

const COUNTRIES = [
  "Australia", "New Zealand", "United Kingdom", "United States", "Canada", "South Africa", "Jamaica", "Singapore", "Malaysia", "India", "Albania",
  "Afghanistan", "Argentina", "Austria", "Bangladesh", "Belgium", "Brazil", "China", "Denmark", "Egypt", "Finland", "France", "Germany", 
  "Greece", "Indonesia", "Ireland", "Italy", "Japan", "Kenya", "Mexico", "Netherlands", "Nigeria", "Norway", "Pakistan", "Philippines", 
  "Portugal", "Russia", "Saudi Arabia", "South Korea", "Spain", "Sri Lanka", "Sweden", "Switzerland", "Thailand", "Turkey", "Vietnam"
];

const PlayerAdd = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { t } = useTranslation();
  const [submitted, setSubmitted] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [clubs, setClubs] = useState([]);
  const dropdownRef = useRef(null);

  const [initialValues, setInitialValues] = useState({
    firstName: "",
    lastName: "",
    email: "",
    dob: "",
    city: "",
    country: "",
    height: "",
    preferredPositions: [],
    clubEmail: "",
    password: "",
    confirmPassword: "",
  });

  const breadcrumbs = [
    { title: t("sidebar_link_dashboard"), url: "/admin/dashboard" },
    { title: t("sidebar_link_players"), url: "/admin/user-management/players/list/1" },
    { title: t("link_add"), url: "" },
  ];

  useEffect(() => {
    clubAdmin.List({ limit: 1000, filters: { isProfileVerified: "verified", isBlocked: false } }).then(res => {
      if (res.success) {
        const activeClubs = (res.data.list || []).filter(
          c => !c.isDeleted && 
               c.isDeleted !== "true" && 
               !c.deletedAt && 
               c.status !== "DELETED" && 
               (c.isProfileVerified === "verified" || c.isProfileVerified === true || c.isProfileVerified === "true") && 
               !c.isBlocked
        );
        setClubs(activeClubs);
      }
    });

    let parsedPositions = [];
    if (state?.preferredPositions) {
      try {
        parsedPositions = Array.isArray(state.preferredPositions)
          ? state.preferredPositions
          : JSON.parse(state.preferredPositions);
      } catch {
        parsedPositions = [];
      }
    }
    setInitialValues({
      firstName: state?.firstName || "",
      lastName: state?.lastName || "",
      email: state?.email || "",
      dob: state?.dob || "",
      city: state?.city || "",
      country: state?.country || "",
      height: state?.height || "",
      preferredPositions: parsedPositions,
      clubEmail: state?.clubEmail || "",
      password: "",
      confirmPassword: "",
    });
    setDataLoaded(true);
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const togglePosition = (key, values, setFieldValue) => {
    const current = values.preferredPositions || [];
    const updated = current.includes(key)
      ? current.filter((p) => p !== key)
      : [...current, key];
    setFieldValue("preferredPositions", updated);
  };

  return (
    <>
      <Breadcrums data={breadcrumbs} />
      {dataLoaded ? (
        <Formik
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnChange={true}
          validate={(values) => {
            const error = {};
            const firstName = values.firstName?.trim() || "";
            const lastName = values.lastName?.trim() || "";
            const email = values.email?.trim() || "";
            const dob = values.dob?.trim() || "";
            const city = values.city?.trim() || "";
            const country = values.country?.trim() || "";
            const height = values.height?.toString().trim() || "";
            const preferredPositions = values.preferredPositions || [];
            const clubEmail = values.clubEmail?.trim() || "";
            const password = values.password?.trim() || "";
            const confirmPassword = values.confirmPassword?.trim() || "";

            // First Name Validation
            if (firstName && firstName.length > INPUT_LENGTH_50)
              error.firstName = t("validation_err_reached_maximum_length");

            // Last Name Validation
            if (lastName && lastName.length > INPUT_LENGTH_50)
              error.lastName = t("validation_err_reached_maximum_length");

            // Email Validation
            if (!email) error.email = t("label_email_error");
            else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email))
              error.email = t("validation_err_invalid_email");

            // DOB Validation
            if (!dob) error.dob = t("label_dob_error");

            // City Validation
            if (!city) error.city = t("label_city_error");
            else if (city.length > INPUT_LENGTH_50)
              error.city = t("validation_err_reached_maximum_length");

            // Country Validation
            if (!country) error.country = t("label_country_error");
            else if (country.length > INPUT_LENGTH_50)
              error.country = t("validation_err_reached_maximum_length");

            // Height Validation
            if (!height) error.height = t("label_height_error");
            else if (isNaN(Number(height))) error.height = t("label_height_error");

            // Preferred Positions Validation
            if (!preferredPositions || preferredPositions.length === 0)
              error.preferredPositions = t("label_preferred_positions_error");

            // Club Email Validation
            if (!clubEmail) error.clubEmail = t("label_club_email_error");
            else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(clubEmail))
              error.clubEmail = t("label_club_email_error");

            // Password Validation
            if (!password) {
              error.password = t("validation_password_required");
            } else if (password.length < 8) {
              error.password = t("Password_Validation_minimum_length");
            } else if (!/[A-Z]/.test(password)) {
              error.password = t("validation_err_password_capital_letter");
            } else if (!/\d/.test(password)) {
              error.password = t("validation_err_password_one_number");
            } else if (!/[@$!%*?&]/.test(password)) {
              error.password = t("validation_err_password_special_char");
            }

            // Confirm Password Validation
            if (!confirmPassword) {
              error.confirmPassword = t("label_confirm_password_error");
            } else if (password !== confirmPassword) {
              error.confirmPassword = t("password_validation_does_not_match");
            }

            return error;
          }}
          onSubmit={(values, { setSubmitting, resetForm }) => {
            setSubmitted(true);
            globalLoader(true);

            let formData = new FormData();
            formData.append("firstName", values.firstName.trim());
            formData.append("lastName", values.lastName.trim());
            formData.append("email", values.email.trim());
            formData.append("dob", values.dob);
            formData.append("city", values.city.trim());
            formData.append("country", values.country.trim());
            formData.append("height", values.height);
            formData.append("preferredPositions", JSON.stringify(values.preferredPositions));
            formData.append("clubEmail", values.clubEmail.trim());
            formData.append("password", values.password);
            formData.append("confirmPassword", values.confirmPassword);
            players
              .Add(formData)
              .then((response) => {
                console.log("response", response);
                setSubmitting(false);
                if (response.success) {
                  Swal.fire({
                    icon: "success",
                    text: response.message,
                    ...SWAL_SETTINGS,
                  });
                  setTimeout(() => {
                    resetForm({ values: "" });
                    globalLoader(false);
                    navigate(`/admin/user-management/players/list/1`);
                  }, 1000);
                } else {
                  Swal.fire({
                    icon: "error",
                    text: handleServerValidations(response),
                    ...SWAL_SETTINGS,
                  });
                  setSubmitted(false);
                  globalLoader(false);
                }
              })
              .catch((error) => {
                console.log("error ====> ", error);
                Swal.fire({
                  icon: "error",
                  text:
                    handleServerValidations(error) ||
                    "Something went wrong. Please try again.",
                  ...SWAL_SETTINGS,
                });
                setSubmitted(false);
                globalLoader(false);
              });
          }}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
          }) => (
            <form onSubmit={handleSubmit} autoComplete="off">
              {/* Hidden dummy fields to trick browser autofill */}
              <input type="text" style={{ display: "none" }} />
              <input type="password" style={{ display: "none" }} />

              <div className="row row-sm">
                <div className="col-lg-12 col-md-12 animation_fade">
                  <div className="card custom-card">
                    <div className="card-body">
                      <div>
                        <h6 className="main-content-label mb-3">{t("add_player")}</h6>
                      </div>
                      <div className="row row-sm">

                        {/* First Name */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="firstName" className="text-left d-flex">
                            {t("label_first_name")}:
                          </label>
                          <input
                            name="firstName"
                            id="firstName"
                            placeholder="Enter First Name"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.firstName}
                            className="form-control"
                            autoComplete="off"
                          />
                           <span className="text-danger d-flex text-left">
                            {errors.firstName && touched.firstName && errors.firstName}
                          </span>
                        </div>

                        {/* Last Name */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="lastName" className="text-left d-flex">
                            {t("label_last_name")}:
                          </label>
                          <input
                            name="lastName"
                            id="lastName"
                            placeholder="Enter Last Name"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.lastName}
                            className="form-control"
                            autoComplete="off"
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.lastName && touched.lastName && errors.lastName}
                          </span>
                        </div>

                        {/* Email */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="email" className="text-left d-flex">
                            {t("label_email")}:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="email"
                            id="email"
                            placeholder="Enter Email"
                            type="email"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.email}
                            className="form-control"
                            autoComplete="new-email"
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.email && touched.email && errors.email}
                          </span>
                        </div>

                        {/* DOB */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="dob" className="text-left d-flex">
                            {t("label_dob")}:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="dob"
                            id="dob"
                            placeholder="Enter Dob"
                            type="date"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.dob}
                            className="form-control"
                            autoComplete="off"
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.dob && touched.dob && errors.dob}
                          </span>
                        </div>

                        {/* City */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="city" className="text-left d-flex">
                            {t("label_city")}:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="city"
                            id="city"
                            placeholder="Enter City"
                            type="text"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.city}
                            className="form-control"
                            autoComplete="off"
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.city && touched.city && errors.city}
                          </span>
                        </div>

                        {/* Country */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="country" className="text-left d-flex">
                            {t("label_country")}:<span className="requirestar">*</span>
                          </label>
                          <select
                            name="country"
                            id="country"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.country}
                            className="form-control"
                          >
                            <option value="">Select Country</option>
                            {COUNTRIES.map((country) => (
                              <option key={country} value={country}>
                                {country}
                              </option>
                            ))}
                          </select>
                          <span className="text-danger d-flex text-left">
                            {errors.country && touched.country && errors.country}
                          </span>
                        </div>

                        {/* Height */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="height" className="text-left d-flex">
                            {t("label_height")}:<span className="requirestar">*</span>
                          </label>
                          <input
                            name="height"
                            id="height"
                            placeholder="Enter Height"
                            type="number"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.height}
                            className="form-control"
                            autoComplete="off"
                          />
                          <span className="text-danger d-flex text-left">
                            {errors.height && touched.height && errors.height}
                          </span>
                        </div>

                        {/* Preferred Positions - Multi-select Dropdown */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="preferredPositions" className="text-left d-flex">
                            {t("Preferred Positions")}:<span className="requirestar">*</span>
                          </label>
                          <div ref={dropdownRef} style={{ position: "relative" }}>
                            <div
                              className="form-control d-flex align-items-center"
                              style={{ cursor: "pointer", minHeight: "38px", flexWrap: "wrap", gap: "4px" }}
                              onClick={() => setDropdownOpen((prev) => !prev)}
                            >
                              {values.preferredPositions && values.preferredPositions.length > 0 ? (
                                values.preferredPositions.map((pos) => (
                                  <span
                                    key={pos}
                                    style={{
                                      background: "#e0e0e0",
                                      borderRadius: "4px",
                                      padding: "2px 8px",
                                      fontSize: "13px",
                                      display: "inline-flex",
                                      alignItems: "center",
                                      gap: "4px",
                                    }}
                                  >
                                    {pos}
                                    <span
                                      style={{ cursor: "pointer", fontWeight: "bold", lineHeight: 1 }}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        togglePosition(pos, values, setFieldValue);
                                      }}
                                    >
                                      ×
                                    </span>
                                  </span>
                                ))
                              ) : (
                                <span style={{ color: "#999" }}>Select Positions</span>
                              )}
                              <span style={{ marginLeft: "auto" }}>▾</span>
                            </div>
                            {dropdownOpen && (
                              <div
                                style={{
                                  position: "absolute",
                                  top: "100%",
                                  left: 0,
                                  right: 0,
                                  background: "#fff",
                                  border: "1px solid #ccc",
                                  borderRadius: "4px",
                                  zIndex: 999,
                                  boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                }}
                              >
                                {POSITIONS.map(({ key, label }) => (
                                  <div
                                    key={key}
                                    onClick={() => togglePosition(key, values, setFieldValue)}
                                    style={{
                                      padding: "8px 12px",
                                      cursor: "pointer",
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "8px",
                                      background: values.preferredPositions?.includes(key) ? "#f0f4ff" : "#fff",
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                                    onMouseLeave={(e) =>
                                    (e.currentTarget.style.background = values.preferredPositions?.includes(key)
                                      ? "#f0f4ff"
                                      : "#fff")
                                    }
                                  >
                                    <input
                                      type="checkbox"
                                      readOnly
                                      checked={values.preferredPositions?.includes(key) || false}
                                      style={{ cursor: "pointer" }}
                                    />
                                    <span style={{ fontWeight: "600", minWidth: "30px" }}>{key}</span>
                                    <span style={{ color: "#666", fontSize: "13px" }}>{label}</span>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <span className="text-danger d-flex text-left">
                            {errors.preferredPositions && touched.preferredPositions && errors.preferredPositions}
                          </span>
                        </div>

                        {/* Club Email */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="clubEmail" className="text-left d-flex">
                            {t("label_club_email")}:<span className="requirestar">*</span>
                          </label>
                          <select
                            name="clubEmail"
                            id="clubEmail"
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.clubEmail}
                            className="form-control"
                          >
                            <option value="">Select Club</option>
                            {clubs.map((club) => (
                              <option key={club.id} value={club.email}>
                                {club.clubDetails?.name || club.firstName}
                              </option>
                            ))}
                          </select>
                          <span className="text-danger d-flex text-left">
                            {errors.clubEmail && touched.clubEmail && errors.clubEmail}
                          </span>
                        </div>

                        {/* Password */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="password" className="text-left d-flex">
                            {t("label_password")}:<span className="requirestar">*</span>
                          </label>
                          <div
                            style={{
                              display: "flex",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <input
                              name="password"
                              id="password"
                              placeholder="Enter Password"
                              type={showPassword ? "text" : "password"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.password}
                              style={{
                                border: "none",
                                flex: 1,
                                padding: "10px",
                                outline: "none",
                              }}
                              autoComplete="new-password"
                            />
                            <div
                              onClick={() => setShowPassword((prev) => !prev)}
                              style={{
                                width: "45px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderLeft: "1px solid #ced4da",
                                cursor: "pointer",
                                background: "#f8f9fa",
                              }}
                            >
                              <i className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`} />
                            </div>
                          </div>
                          <span className="text-danger d-flex text-left">
                            {errors.password && touched.password && errors.password}
                          </span>
                        </div>

                        {/* Confirm Password */}
                        <div className="col-lg-6 text-center form-group">
                          <label htmlFor="confirmPassword" className="text-left d-flex">
                            {t("label_confirm_password")}:<span className="requirestar">*</span>
                          </label>
                          <div
                            style={{
                              display: "flex",
                              border: "1px solid #ced4da",
                              borderRadius: "6px",
                              overflow: "hidden",
                            }}
                          >
                            <input
                              name="confirmPassword"
                              id="confirmPassword"
                              placeholder="Enter Confirm Password"
                              type={showConfirmPassword ? "text" : "password"}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              value={values.confirmPassword}
                              style={{
                                border: "none",
                                flex: 1,
                                padding: "10px",
                                outline: "none",
                              }}
                              autoComplete="new-password"
                            />
                            <div
                              onClick={() => setShowConfirmPassword((prev) => !prev)}
                              style={{
                                width: "45px",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                borderLeft: "1px solid #ced4da",
                                cursor: "pointer",
                                background: "#f8f9fa",
                              }}
                            >
                              <i className={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`} />
                            </div>
                          </div>
                          <span className="text-danger d-flex text-left">
                            {errors.confirmPassword && touched.confirmPassword && errors.confirmPassword}
                          </span>
                        </div>

                        {/* Buttons */}
                        <div className="">
                          <button
                            className="btn btn-main-primary signbtn mr-2"
                            type="submit"
                            disabled={submitted ? true : null}
                          >
                            <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                            {t("btn_submit")}
                          </button>
                          <button
                            className="btn ripple btn-secondary"
                            type="button"
                            disabled={submitted ? true : null}
                            onClick={() => navigate(-1)}
                          >
                            <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                            {t("btn_cancel")}
                          </button>
                        </div>

                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          )}
        </Formik>
      ) : (
        <></>
      )}
    </>
  );
};

export default PlayerAdd;